import { useEffect, useState } from "react";
import { ArrowDownToLine, ArrowUpFromLine, Save, X } from "lucide-react";
import { toast } from "sonner";
import { api, CURRENCIES, errMsg, PAY_STATUSES, STAGES } from "../api";

const today = () => new Date().toISOString().slice(0, 10);

export default function QuickPaymentModal({ close, onSaved, preset = {} }) {
  const [dir, setDir] = useState(preset.direction || "outgoing");
  const [projects, setProjects] = useState([]);
  const [subs, setSubs] = useState([]);
  const [workItems, setWorkItems] = useState([]);
  const [f, setF] = useState({
    project_id: preset.project_id || "", work_item_id: preset.work_item_id || "", subcontractor_short: preset.subcontractor_short || "",
    stage: preset.direction === "incoming" ? "Tahsilat" : "Avans", amount: "", currency: "TRY", vat_included: false, vat_rate: "20", date: today(), due_date: "", status: "Ödendi", description: "",
  });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const prj = projects.find((p) => p.id === f.project_id);

  const amt = Number(f.amount) || 0;
  const rate = (Number(f.vat_rate) || 0) / 100;
  const vatNet = f.vat_included ? (rate ? amt / (1 + rate) : amt) : amt;
  const vatGross = f.vat_included ? amt : amt * (1 + rate);
  const vatAmount = vatGross - vatNet;
  const fmt = (n) => n.toLocaleString("tr-TR", { maximumFractionDigits: 2 });

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data));
    api.get("/subcontractors").then((r) => setSubs(r.data));
  }, []);
  useEffect(() => {
    if (!f.project_id) { setWorkItems([]); return; }
    api.get(`/work-items?project_id=${f.project_id}`).then((r) => setWorkItems(r.data));
    const p = projects.find((x) => x.id === f.project_id);
    if (p) setF((s) => ({ ...s, currency: p.currency || "TRY" }));
  }, [f.project_id, projects]);

  const pickWi = (e) => {
    const w = workItems.find((x) => x.id === e.target.value);
    setF((s) => ({ ...s, work_item_id: e.target.value, subcontractor_short: w?.subcontractor_short || s.subcontractor_short }));
  };
  const switchDir = (d) => { setDir(d); setF((s) => ({ ...s, stage: d === "incoming" ? "Tahsilat" : "Avans", work_item_id: "", subcontractor_short: "" })); };

  const submit = async (e) => {
    e.preventDefault();
    if (!prj) return toast.error("Proje seçin");
    const wi = workItems.find((x) => x.id === f.work_item_id);
    try {
      await api.post("/payments", {
        ...f, direction: dir, project_name: prj.name, amount: Number(f.amount) || 0,
        vat_included: !!f.vat_included, vat_rate: Number(f.vat_rate) || 0,
        date: f.status === "Ödendi" ? f.date || today() : f.date || null, due_date: f.due_date || null,
        work_item_id: f.work_item_id || null, work_item_category: wi?.category || null,
        subcontractor_short: dir === "incoming" ? null : f.subcontractor_short || null,
      });
      toast.success(dir === "incoming" ? "Tahsilat kaydedildi" : "Alt yüklenici ödemesi kaydedildi");
      onSaved?.(); close();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="modal-backdrop" onClick={close}>
      <form className="modal modal-wide" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="quick-payment-modal">
        <div className="modal-head">
          <div><span className="eyebrow">HIZLI ÖDEME KAYDI</span><h2>{dir === "incoming" ? "İşverenden tahsilat" : "Alt yükleniciye ödeme"}</h2></div>
          <button type="button" onClick={close} data-testid="quick-payment-close"><X size={18} /></button>
        </div>
        <div className="dir-tabs">
          <button type="button" className={dir === "incoming" ? "selected in" : ""} onClick={() => switchDir("incoming")} data-testid="quick-pay-tab-incoming"><ArrowDownToLine size={15} /> İşverenden Tahsilat</button>
          <button type="button" className={dir === "outgoing" ? "selected out" : ""} onClick={() => switchDir("outgoing")} data-testid="quick-pay-tab-outgoing"><ArrowUpFromLine size={15} /> Alt Yükleniciye Ödeme</button>
        </div>
        <label>Proje
          <select required value={f.project_id} onChange={set("project_id")} data-testid="quick-pay-project-select">
            <option value="">Proje seçin…</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.employer_short}</option>)}
          </select>
        </label>
        {prj && <div className="mini-info" data-testid="quick-pay-project-info">İşveren: <b>{prj.employer_short}</b> · Bedel {prj.agreed_amount?.toLocaleString("tr-TR")} {prj.currency} · Tahsil edilen {prj.paid_amount?.toLocaleString("tr-TR")} {prj.currency}</div>}
        {dir === "outgoing" && (
          <div className="form-grid">
            <label>İş kalemi
              <select value={f.work_item_id} onChange={pickWi} data-testid="quick-pay-wi-select">
                <option value="">— Seçiniz (opsiyonel) —</option>
                {workItems.map((w) => <option key={w.id} value={w.id}>{w.category}{w.subcontractor_short ? ` · ${w.subcontractor_short}` : ""}</option>)}
              </select>
            </label>
            <label>Alt yüklenici
              <input list="qp-subs" required value={f.subcontractor_short} onChange={set("subcontractor_short")} placeholder="Kısa ad" data-testid="quick-pay-sub-input" />
              <datalist id="qp-subs">{subs.map((s) => <option key={s.id} value={s.short_name}>{s.name}</option>)}</datalist>
            </label>
          </div>
        )}
        <div className="form-grid three">
          <label>Tutar (KDV {f.vat_included ? "dahil" : "hariç"})<input required type="number" step="0.01" min="0" value={f.amount} onChange={set("amount")} data-testid="quick-pay-amount-input" /></label>
          <label>Para birimi<select value={f.currency} onChange={set("currency")} data-testid="quick-pay-currency-select">{CURRENCIES.map((c) => <option key={c}>{c}</option>)}</select></label>
          <label>Aşama<select value={f.stage} onChange={set("stage")} data-testid="quick-pay-stage-select">{STAGES.map((s) => <option key={s}>{s}</option>)}</select></label>
        </div>
        <div className="vat-row" data-testid="quick-pay-vat-row">
          <label className="vat-check">
            <input type="checkbox" checked={!!f.vat_included} onChange={(e) => setF((s) => ({ ...s, vat_included: e.target.checked }))} data-testid="quick-pay-vat-included" />
            <span>Girilen tutar KDV dahil</span>
          </label>
          <label className="vat-rate">KDV oranı %<input type="number" step="1" min="0" value={f.vat_rate} onChange={set("vat_rate")} data-testid="quick-pay-vat-rate" /></label>
          {amt > 0 && (
            <div className="vat-preview" data-testid="quick-pay-vat-preview">
              <span>Net: <b>{fmt(vatNet)} {f.currency}</b></span>
              <span>KDV: <b>{fmt(vatAmount)} {f.currency}</b></span>
              <span>Brüt: <b>{fmt(vatGross)} {f.currency}</b></span>
            </div>
          )}
        </div>
        <div className="form-grid three">
          <label>Durum<select value={f.status} onChange={set("status")} data-testid="quick-pay-status-select">{PAY_STATUSES.map((s) => <option key={s}>{s}</option>)}</select></label>
          <label>{f.status === "Ödendi" ? "Ödeme tarihi" : "Planlanan tarih"}<input type="date" value={f.date} onChange={set("date")} data-testid="quick-pay-date-input" /></label>
          <label>Vade tarihi<input type="date" value={f.due_date} onChange={set("due_date")} data-testid="quick-pay-due-input" /></label>
        </div>
        <label>Açıklama<input value={f.description} onChange={set("description")} placeholder="Fatura no, hakediş, not…" data-testid="quick-pay-desc-input" /></label>
        <button className={`new-btn full ${dir === "incoming" ? "btn-green" : ""}`} data-testid="quick-pay-save-button"><Save size={15} /> {dir === "incoming" ? "Tahsilatı kaydet" : "Ödemeyi kaydet"}</button>
      </form>
    </div>
  );
}
